/**
 * Live results of a public survey — /public/forms/{token}/results (TRUS-624).
 *
 * No login. Only a link whose admin turned results on answers at all; every
 * other token is a 404. The server sends aggregate choice-question counts only
 * — never free text, never the optional contact section — and nothing at all
 * until enough people have answered that one person's answers cannot be picked
 * out.
 *
 * "Live" is a 30-second refresh while the tab is visible. Results move with
 * each submission at a community event, and a websocket for a page this small
 * would be machinery without a reader.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, AlertCircle, Languages, RefreshCw, ArrowLeft, Lock } from 'lucide-react';
import { config as productConfig } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useDynamicTranslation } from './dynamicTranslation';
import { mergeFormTheme, themeToCssVars, themePageBackgroundStyle, type PartialFormTheme } from './formTheme';
import SurveyResultsView, { type SurveyResultsData } from './SurveyResultsView';

const API_BASE = (typeof window !== 'undefined' ? (productConfig.weaveinApi.baseUrl ?? '') : '').trim().replace(/\/$/, '');
const REFRESH_MS = 30_000;
const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
] as const;

interface PublicResults extends SurveyResultsData {
  title: string;
  description?: string;
  organization_name?: string;
  theme?: PartialFormTheme;
  form_url?: string | null;
  min_responses: number;
  suppressed: boolean;
}

function readLangParam(): string {
  if (typeof window === 'undefined') return 'en';
  const raw = (new URLSearchParams(window.location.search).get('lang') || '').toLowerCase().split('-')[0];
  return raw === 'es' ? 'es' : 'en';
}

export default function PublicSurveyResultsPage({ token }: { token: string }) {
  const { t, language, setLanguage } = useAppTranslation();
  const [lang, setLang] = useState<string>(readLangParam);
  // Declared above useDynamicTranslation: a const read before its line is in the
  // temporal dead zone and blanks the page in the production build (TRUS-418).
  const [bundle, setBundle] = useState<Record<string, string> | null>(null);
  const tr = useDynamicTranslation(bundle);
  const [data, setData] = useState<PublicResults | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await fetch(`${API_BASE}/api/public/forms/${token}/results`, { cache: 'no-store' });
      if (!res.ok) {
        setError(res.status === 404 ? 'not_found' : 'failed');
        return;
      }
      setData(await res.json());
      setError(null);
    } catch {
      setError('failed');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  // Same token-scoped translation bundle the survey page uses.
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/public/forms/${token}/translations/${lang}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => {
        if (!cancelled) setBundle(b?.translations || null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [token, lang]);

  useEffect(() => {
    if (lang !== language) void setLanguage(lang);
    // Mount-only: after this the toggle owns the language.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chooseLanguage = (code: string) => {
    setLang(code);
    void setLanguage(code);
  };

  const rawTheme = data?.theme;
  const theme = mergeFormTheme(rawTheme);
  const accent = theme.colors.primary;
  const pageStyle = { ...themeToCssVars(rawTheme), ...themePageBackgroundStyle(rawTheme) };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" aria-label={t('common.loading', 'Loading')} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="p-6 bg-white rounded-xl shadow-sm max-w-md text-center">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" aria-hidden="true" />
          <h1 className="text-lg font-semibold text-gray-900 mb-1">
            {error === 'not_found'
              ? t('survey_results.not_available', 'Results are not available')
              : t('survey_results.load_failed', 'Could not load results')}
          </h1>
        </div>
      </div>
    );
  }

  const formHref = data.form_url ? `${data.form_url}${lang === 'es' ? '?lang=es' : ''}` : null;

  return (
    <div className="min-h-screen py-8 px-4" style={pageStyle}>
      <div className="mx-auto max-w-3xl">
        {/* Header card on the theme's surface colour: its muted text is not
            readable on an org's gradient page background (TRUS-624). */}
        <header
          className="mb-6 rounded-xl shadow-sm px-5 pt-4 pb-5"
          style={{ backgroundColor: theme.colors.surface }}
        >
        <div className="mb-2 flex items-center justify-between gap-2">
          {formHref ? (
            <a href={formHref} className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: accent }}>
              <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" />
              {t('survey_results.take_survey', 'Take the survey')}
            </a>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-1">
            <Languages className="w-4 h-4 mr-1" style={{ color: theme.colors.muted }} aria-hidden="true" />
            {LANGUAGES.map((option) => {
              const active = lang === option.code;
              return (
                <button
                  key={option.code}
                  type="button"
                  onClick={() => chooseLanguage(option.code)}
                  aria-pressed={active}
                  lang={option.code}
                  className="px-3 py-1 text-xs font-medium rounded-full border transition-colors"
                  style={{
                    borderColor: active ? accent : theme.colors.border,
                    backgroundColor: active ? accent : 'transparent',
                    color: active ? '#fff' : theme.colors.muted,
                  }}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="text-center">
          {data.organization_name && (
            <p className="text-sm mb-1" style={{ color: theme.colors.muted }}>
              {data.organization_name}
            </p>
          )}
          <h1 className="text-2xl font-bold" style={{ color: theme.colors.text }}>
            {tr.publicForm('title', data.title)}
          </h1>
          <p className="mt-2 inline-flex items-center gap-1.5 text-xs" style={{ color: theme.colors.muted }}>
            <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
            {t('survey_results.live_note', 'Live results — updates automatically')}
          </p>
        </div>
        </header>

        {data.suppressed ? (
          <div className="rounded-xl bg-white p-8 text-center shadow-sm">
            <Lock className="w-8 h-8 mx-auto mb-3 text-gray-400" aria-hidden="true" />
            <p className="text-sm text-gray-700">
              {t(
                'survey_results.waiting_for_responses',
                `Results appear once ${data.min_responses} people have answered, so no one's answers can be singled out.`,
                { count: data.min_responses },
              )}
            </p>
            <p className="mt-2 text-xs text-gray-500" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {t('survey_results.responses_so_far', `${data.total_responses} so far`, { count: data.total_responses })}
            </p>
          </div>
        ) : (
          <SurveyResultsView data={data} tr={tr} accent={accent} />
        )}

        <p
          className="mt-6 mx-auto w-fit rounded-full px-4 py-2 text-center text-xs"
          style={{ color: theme.colors.muted, backgroundColor: theme.colors.surface }}
        >
          {t(
            'survey_results.privacy_note',
            'Only totals are shown. Written answers and contact details are never published.',
          )}
        </p>
      </div>
    </div>
  );
}
